// @ts-check
// Conversão de filtros de view (operandos do Twenty, doc 06 seção 10) para a árvore de filtro REST. As regras moram em
// shared/filter/view-filter-tree.js, as mesmas do público no motor (contrato 09 de público, seção 3); aqui a SPA monta o
// contexto de quem está usando (agora arredondado ao minuto, fuso, primeiro dia da semana e o membro do "Eu").
import { FIELD_TYPE, findOption, formatFullName } from './field-metadata.js';
import { formatDate, formatDateTime, getFormatPreferences } from './format.js';
import { t, translateMetadataLabel } from '../i18n/index.js';
import { currentWorkspaceMemberState } from '../data/metadata-store.js';
import { firstDayOfWeekFromCalendarStartDay, parseRelativeDateValue } from '../../../shared/filter/relative-date.js';
import {
  buildFilterTree as buildSharedFilterTree,
  exactInstantOf,
  getOperandsForField,
  operandNeedsValue,
  parseListValue,
  isCurrentWorkspaceMemberSelected,
  relativeValueInContext,
  viewFilterToFilterNode as sharedViewFilterToFilterNode,
} from '../../../shared/filter/view-filter-tree.js';

export {
  FILTER_SUB_FIELDS,
  exactInstantOf,
  getFilterSubFields,
  getOperandsForField,
  isCurrentWorkspaceMemberSelected,
  operandNeedsValue,
  parseListValue,
} from '../../../shared/filter/view-filter-tree.js';

/**
 * @typedef {import('./field-metadata.js').FieldMetadata} FieldMetadata
 * @typedef {import('./rest-filter.js').FilterNode} FilterNode
 * @typedef {import('../../../shared/filter/view-filter-tree.js').ViewFilter} ViewFilter
 * @typedef {import('../../../shared/filter/view-filter-tree.js').ViewFilterGroup} ViewFilterGroup
 * @typedef {import('../../../shared/filter/view-filter-tree.js').FilterConversionContext} FilterConversionContext
 */

/** Rótulo de um sub-campo ("Cidade", "Sobrenome"). @param {string} subFieldName */
export const getSubFieldLabel = (subFieldName) => t(`viewsFilters.subFields.${subFieldName}`);

/**
 * "Agora" arredondado ao minuto: o filtro fica igual entre renderizações e a lista não recarrega sem parar
 * (o kanban refaz a busca quando a árvore de filtro muda).
 */
function currentMinuteIso() {
  return new Date(Math.floor(Date.now() / 60_000) * 60_000).toISOString();
}

/**
 * Contexto da conversão na SPA: o minuto atual, o fuso e o primeiro dia da semana das preferências de quem está usando
 * e o membro do "Eu".
 * @returns {FilterConversionContext}
 */
export function currentFilterConversionContext() {
  const preferences = getFormatPreferences();
  return {
    now: currentMinuteIso(),
    timeZone: preferences.timeZone,
    firstDayOfWeek: firstDayOfWeekFromCalendarStartDay(preferences.calendarStartDay),
    currentWorkspaceMemberId: currentWorkspaceMemberState.value?.id ?? null,
  };
}

/**
 * Valor de data relativa com o fuso e o primeiro dia da semana de quem está usando, quando a view não gravou outro.
 * @param {string} rawValue
 */
export function withMemberTimeZone(rawValue) {
  return relativeValueInContext(rawValue, currentFilterConversionContext());
}

/**
 * Texto de uma data relativa: "Últimos 7 dias", "Este mês", "Próximas 2 semanas".
 * @param {string} rawValue
 */
export function formatRelativeDateLabel(rawValue) {
  const parsed = parseRelativeDateValue(rawValue);
  if (!parsed) return '';
  const unit = parsed.unit.toLowerCase();
  if (parsed.direction === 'THIS') return t(`viewsFilters.relative.this.${unit}`);
  return t(`viewsFilters.relative.${parsed.direction === 'PAST' ? 'past' : 'next'}.${unit}`, { count: parsed.amount ?? 1 });
}

/**
 * Gera o nó da árvore de filtro para um filtro de view, no contexto de quem está usando (ou no contexto dado).
 * @param {ViewFilter} viewFilter
 * @param {FieldMetadata} field
 * @param {FilterConversionContext} [context]
 * @returns {FilterNode | null}
 */
export function viewFilterToFilterNode(viewFilter, field, context = currentFilterConversionContext()) {
  return /** @type {FilterNode | null} */ (sharedViewFilterToFilterNode(viewFilter, field, context));
}

/**
 * Filtros simples (sem grupo) juntos por E, mais o filtro avançado: o grupo raiz com suas regras e subgrupos, cada
 * grupo combinando os filhos com o próprio operador (doc 06, seções 10 e 11). Regras incompletas não entram.
 * @param {ViewFilter[]} viewFilters
 * @param {(fieldMetadataId: string) => FieldMetadata | undefined} getField
 * @param {ViewFilterGroup[]} [filterGroups]
 * @param {FilterConversionContext} [context]
 * @returns {FilterNode | null}
 */
export function buildFilterTree(viewFilters, getField, filterGroups = [], context = currentFilterConversionContext()) {
  return /** @type {FilterNode | null} */ (buildSharedFilterTree(viewFilters, getField, filterGroups, context));
}

/**
 * Valor exibido no chip do filtro.
 * @param {ViewFilter} viewFilter
 * @param {FieldMetadata} field
 */
export function getFilterDisplayValue(viewFilter, field) {
  if (!operandNeedsValue(viewFilter.operand)) return '';
  if (viewFilter.displayValue) return viewFilter.displayValue;
  const rawValue = viewFilter.value ?? '';
  if (viewFilter.operand === 'IS_RELATIVE') return formatRelativeDateLabel(rawValue);
  switch (field.type) {
    case FIELD_TYPE.SELECT:
    case FIELD_TYPE.MULTI_SELECT:
      return parseListValue(rawValue)
        .map((value) => translateMetadataLabel(findOption(field, value)?.label ?? value))
        .join(', ');
    case FIELD_TYPE.RELATION: {
      const count = parseListValue(rawValue).length;
      if (isCurrentWorkspaceMemberSelected(rawValue)) {
        return count > 0 ? `${t('filters.currentWorkspaceMember')}, ${t('filters.recordsSelected', { count })}` : t('filters.currentWorkspaceMember');
      }
      return t('filters.recordsSelected', { count });
    }
    case FIELD_TYPE.BOOLEAN:
      return rawValue === 'true' ? t('common.true') : t('common.false');
    case FIELD_TYPE.DATE:
      return formatDate(rawValue);
    case FIELD_TYPE.DATE_TIME:
      // instante exato aparece com a hora no fuso de quem vê
      return exactInstantOf(rawValue) ? formatDateTime(rawValue) : formatDate(rawValue);
    case FIELD_TYPE.RATING:
      return '★'.repeat(Number(String(rawValue).replace('RATING_', '')) || 0);
    case FIELD_TYPE.FULL_NAME:
      return typeof rawValue === 'object' ? formatFullName(rawValue) : rawValue;
    default:
      return rawValue;
  }
}

/**
 * @param {FieldMetadata} field
 * @param {string | null} [subFieldName]
 */
export function getDefaultOperand(field, subFieldName = null) {
  return getOperandsForField(field, subFieldName)[0];
}
